import { writable, derived } from 'svelte/store';

// Define our breakpoints (optimized for elite UX experience)
export const breakpoints = {
  xs: 0,      // Extra small devices
  sm: 576,    // Small devices (phones)
  md: 768,    // Medium devices (tablets) - start earlier
  lg: 991,    // Skip the problematic middle size
  xl: 1200,   // Extra large devices (large desktops)
  xxl: 1600   // Ultra wide screens
};

// Create a writable store for window dimensions
const createScreenSizeStore = () => {
  // Initialize with sensible defaults (will be immediately updated)
  const { subscribe, set, update } = writable({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
    orientation: 'landscape'
  });

  // Return the store with custom methods
  return {
    subscribe,
    set,
    // Initialize the store and set up listeners
    init: () => {
      if (typeof window === 'undefined') return; // Skip on SSR

      // Function to update dimensions
      const updateDimensions = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        set({
          width,
          height,
          orientation: width >= height ? 'landscape' : 'portrait'
        });
      };

      // Set initial dimensions
      updateDimensions();

      // Throttle function to limit update frequency
      let resizeTimeout;
      const throttledUpdateDimensions = () => {
        if (!resizeTimeout) {
          resizeTimeout = setTimeout(() => {
            resizeTimeout = null;
            updateDimensions();
          }, 100); // 100ms throttle
        }
      };

      // Add event listener with throttling
      window.addEventListener('resize', throttledUpdateDimensions);
      
      // Also listen for orientation change for mobile devices
      window.addEventListener('orientationchange', updateDimensions);

      // Return cleanup function
      return () => {
        window.removeEventListener('resize', throttledUpdateDimensions);
        window.removeEventListener('orientationchange', updateDimensions);
      };
    }
  };
};

// Create the base screen size store
export const screenSize = createScreenSizeStore();

// Derived store for current breakpoint
export const currentBreakpoint = derived(screenSize, ($screenSize) => {
  // Find the largest breakpoint that the current width exceeds
  const width = $screenSize.width;
  
  if (width >= breakpoints.xxl) return 'xxl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
});

// Helper derived stores for specific checks (for easy use in components)
export const isMobile = derived(currentBreakpoint, ($bp) => ['xs', 'sm'].includes($bp));
export const isTablet = derived(currentBreakpoint, ($bp) => $bp === 'md');
export const isDesktop = derived(currentBreakpoint, ($bp) => ['lg', 'xl', 'xxl'].includes($bp));
export const isLargeDesktop = derived(currentBreakpoint, ($bp) => ['xl', 'xxl'].includes($bp));

// Helper for matching media queries directly
export const matches = (query) => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(query).matches;
};

// Add a reactive matches store for any media query
export function createMediaQueryStore(query) {
  const { subscribe, set } = writable(false);
  
  if (typeof window !== 'undefined') {
    // Initial value
    set(window.matchMedia(query).matches);
    
    // Update on change
    const mediaQueryList = window.matchMedia(query);
    const handler = event => set(event.matches);
    
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handler);
    } else {
      // Fallback for older browsers
      mediaQueryList.addListener(handler);
    }
  }
  
  return { subscribe };
}

// Media query derived stores (for more complex queries)
export const mediaQueries = {
  dark: '(prefers-color-scheme: dark)',
  light: '(prefers-color-scheme: light)',
  reducedMotion: '(prefers-reduced-motion: reduce)',
  highContrast: '(prefers-contrast: more)',
  coarsePointer: '(pointer: coarse)', // touch input
  finePointer: '(pointer: fine)'    // mouse/stylus
};

// More derived stores for specific features
export const prefersDarkMode = derived(screenSize, () => matches(mediaQueries.dark));
export const prefersReducedMotion = derived(screenSize, () => matches(mediaQueries.reducedMotion));
export const hasTouchInput = derived(screenSize, () => matches(mediaQueries.coarsePointer));

// Initialize store when this module is imported on the client
if (typeof window !== 'undefined') {
  screenSize.init();
}